import {
  Button,
  Card,
  Divider,
  Group,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../../components/shared/PageHeader';
import { SessionPickerModal } from '../../../features/session-groups';
import { invalidateSessionGroupQueries } from '../../../features/session-groups';
import { sessionGroupStore } from '../../../features/session-groups';
import { useAuthUser } from '../../../providers/AuthProvider';

type SelectedSession = {
  sessionId: number;
  patientCode?: string | null;
};

export function SessionGroupNewPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthUser();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedSessions, setSelectedSessions] = useState<SelectedSession[]>(
    []
  );
  const [isSaving, setIsSaving] = useState(false);
  const [pickerOpened, { open: openPicker, close: closePicker }] =
    useDisclosure(false);

  const existingSessionIds = selectedSessions.map(session => session.sessionId);

  const handleAddSessions = (
    entries: Array<{
      sessionId: number;
      patientCode?: string | null;
    }>
  ) => {
    setSelectedSessions(prev => {
      const known = new Set(prev.map(session => session.sessionId));
      const additions = entries.filter(entry => !known.has(entry.sessionId));
      return [...prev, ...additions];
    });
    closePicker();
  };

  const handleRemoveSession = (sessionId: number) => {
    setSelectedSessions(prev =>
      prev.filter(session => session.sessionId !== sessionId)
    );
  };

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      notifications.show({
        color: 'red',
        title: 'Nome richiesto',
        message: 'Inserisci un nome per il gruppo.',
      });
      return;
    }

    setIsSaving(true);
    try {
      await sessionGroupStore.createGroup({
        name: trimmedName,
        description: description.trim() || undefined,
        sessionIds: selectedSessions.map(session => session.sessionId),
      });
      invalidateSessionGroupQueries(queryClient);
      notifications.show({
        color: 'teal',
        title: 'Gruppo creato',
        message: 'Il gruppo di sessioni è stato salvato.',
      });
      navigate('/sessions/group');
    } catch (error) {
      notifications.show({
        color: 'red',
        title: 'Salvataggio non riuscito',
        message:
          error instanceof Error
            ? error.message
            : 'Impossibile creare il gruppo.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <PageHeader
        title='Nuovo gruppo sessioni'
        subtitle='Definisci un nome e seleziona le sessioni completed da includere nel gruppo.'
        breadcrumbs={[
          { label: 'Sessioni', href: '/sessions' },
          { label: 'Gruppi sessioni', href: '/sessions/group' },
          { label: 'Nuovo gruppo' },
        ]}
        backUrl='/sessions/group'
      />

      <Divider my='md' />

      <Stack gap='md' maw={720}>
        <TextInput
          label='Nome'
          placeholder='Es. Studio pilota Q1'
          value={name}
          onChange={event => setName(event.currentTarget.value)}
          required
        />
        <Textarea
          label='Descrizione'
          placeholder='Note opzionali sul gruppo'
          value={description}
          onChange={event => setDescription(event.currentTarget.value)}
          minRows={2}
          autosize
        />

        <Card withBorder>
          <Group justify='space-between' mb='sm'>
            <Title order={5}>Sessioni selezionate</Title>
            <Button
              variant='light'
              size='compact-sm'
              leftSection={<Plus size={14} />}
              onClick={openPicker}
            >
              Aggiungi sessioni
            </Button>
          </Group>

          {selectedSessions.length === 0 ? (
            <Text size='sm' c='dimmed'>
              Nessuna sessione selezionata. Puoi salvare il gruppo vuoto e
              aggiungere sessioni in seguito.
            </Text>
          ) : (
            <Table withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>ID sessione</Table.Th>
                  <Table.Th>Utente</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {selectedSessions.map(session => (
                  <Table.Tr key={session.sessionId}>
                    <Table.Td>{session.sessionId}</Table.Td>
                    <Table.Td>{session.patientCode || '—'}</Table.Td>
                    <Table.Td>
                      <Button
                        variant='subtle'
                        color='red'
                        size='compact-sm'
                        leftSection={<Trash2 size={14} />}
                        onClick={() => handleRemoveSession(session.sessionId)}
                      >
                        Rimuovi
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Card>

        <Group>
          <Button onClick={handleSave} loading={isSaving}>
            Salva gruppo
          </Button>
          <Button variant='outline' onClick={() => navigate('/sessions/group')}>
            Annulla
          </Button>
        </Group>
      </Stack>

      <SessionPickerModal
        opened={pickerOpened}
        onClose={closePicker}
        userId={user?.id?.toString()}
        existingSessionIds={existingSessionIds}
        onAddSessions={handleAddSessions}
      />
    </>
  );
}
